import { io } from 'socket.io-client'
import config from 'aldoo-ra/Config'
import actionRegistry from './action-registry'

function getDeviceId() {
  if (typeof window === 'undefined') return null
  let did = localStorage.getItem('_aa_did')
  if (!did) {
    did = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
    localStorage.setItem('_aa_did', did)
  }
  return did
}

function getStoredSessionId(agentId) {
  try { return localStorage.getItem(`_aa_sid_${agentId}`) || null } catch { return null }
}

function storeSessionId(agentId, sid) {
  try { localStorage.setItem(`_aa_sid_${agentId}`, sid) } catch {}
}

function clearStoredSessionId(agentId) {
  try { localStorage.removeItem(`_aa_sid_${agentId}`) } catch {}
}

// Singleton map keyed by agentId
const controllers = new Map()

function createController(agentId) {
  let socket = null
  let sessionId = null
  let pendingMessages = []
  const listeners = {}

  function emit(event, ...args) {
    const cbs = listeners[event]
    if (!cbs) return
    cbs.forEach(cb => cb(...args))
  }

  const controller = {
    connect(userId = null) {
      // Already have an active socket — don't create another one
      if (socket) {
        if (socket.connected) return
        // Socket exists but is disconnected/reconnecting — clean it up first
        socket.removeAllListeners()
        socket.disconnect()
        socket = null
      }

      const deviceId = getDeviceId()
      const url = `${config.PLATFORM_API_URL}/rt/agent`

      socket = io(url, {
        auth: { agentId, deviceId, userId },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
      })

      socket.on('aa:session',   ({ sessionId: sid }) => { if (sid) { sessionId = sid; storeSessionId(agentId, sid) } })
      socket.on('aa:received',  data => emit('received', data))
      socket.on('aa:chunk',     data => emit('chunk', data))
      socket.on('aa:done',      data => emit('done', data))
      socket.on('aa:function', async data => {
        const { name, params, requestId } = data
        // Always notify widget listeners (handles UI-level events like suggest_actions)
        emit('function', data)
        // If backend is waiting for a result, execute the action and respond
        if (requestId) {
          let result = { success: true }
          if (actionRegistry.has(name)) {
            try { result = await actionRegistry.execute(name, params || {}) ?? { success: true } }
            catch (err) { console.warn('[AldooAgent] action error:', name, err) }
          }
          socket.emit(`aa:function:result:${requestId}`, result)
        } else if (actionRegistry.has(name)) {
          actionRegistry.execute(name, params || {}).catch(err =>
            console.warn('[AldooAgent] action error:', name, err)
          )
        }
      })
      socket.on('aa:error', data => {
        if (data?.error === 'session_expired' || data?.error === 'not_found') {
          clearStoredSessionId(agentId)
          sessionId = null
        }
        emit('error', data)
      })
      socket.on('connect_error', err => emit('error', { error: err.message }))
    },

    disconnect() {
      if (!socket) return
      socket.removeAllListeners()
      socket.disconnect()
      socket = null
      sessionId = null
      pendingMessages = []
    },

    trigger(data) {
      if (!socket) return
      const storedSid = getStoredSessionId(agentId)
      const payload = storedSid ? { ...data, resumeSessionId: storedSid } : data
      socket.emit('aa:trigger', payload, (ack) => {
        const sid = ack?.data?.sessionId
        if (sid) {
          sessionId = sid
          storeSessionId(agentId, sid)
          // If backend resumed and returned prior messages, surface them
          if (ack.data.resumed && ack.data.messages?.length) {
            emit('resumed', { messages: ack.data.messages })
          }
          // Flush any messages that arrived before the session was established
          for (const pm of pendingMessages) {
            socket.emit('aa:send_message', { ...pm, sessionId })
          }
          pendingMessages = []
        }
      })
    },

    sendMessage(text, behavior = {}) {
      if (!socket) return
      if (!sessionId) {
        // Session not yet established — queue and send once trigger ack arrives
        pendingMessages.push({ message: text, behavior })
        return
      }
      socket.emit('aa:send_message', { message: text, sessionId, behavior })
    },

    typing() {
      if (!socket) return
      socket.emit('aa:typing')
    },

    on(event, cb) {
      if (!listeners[event]) listeners[event] = []
      listeners[event].push(cb)
    },

    off(event, cb) {
      if (!listeners[event]) return
      listeners[event] = listeners[event].filter(fn => fn !== cb)
    },

    get connected() {
      return socket?.connected ?? false
    },

    get hasSession() {
      return !!sessionId
    },
  }

  return controller
}

export function getController(agentId) {
  if (!controllers.has(agentId)) {
    controllers.set(agentId, createController(agentId))
  }
  return controllers.get(agentId)
}

export default { getController }
